import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  DateRange as DateRangeIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { expensesAPI, usersAPI } from '../services/api';

// Extracted ExpenseDialog component to prevent re-creation on every render
const ExpenseDialog = ({ 
  open, 
  onClose, 
  isEditing, 
  submitting, 
  expenseForm, 
  categories, 
  handleInputChange, 
  handleCategoryChange, 
  setError, 
  setExpenseForm,
  handleCreateExpense,
  handleUpdateExpense,
  isMobile,
  error,
  t
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
    <DialogTitle sx={{ 
      position: isMobile ? 'sticky' : 'static',
      top: 0,
      zIndex: 1,
      backgroundColor: 'background.paper',
      borderBottom: isMobile ? 1 : 0,
      borderColor: 'divider'
    }}>
      {isEditing ? t('buildingExpenses.editExpense') : t('buildingExpenses.addNewExpense')}
    </DialogTitle>
    <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t('buildingExpenses.expenseDate')}
              value={expenseForm.expenseDate}
              onChange={(date) => handleInputChange('expenseDate', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('buildingExpenses.amount')}
              type="number"
              value={expenseForm.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
              error={expenseForm.amount !== '' && (!Number.isFinite(Number(expenseForm.amount)) || Number(expenseForm.amount) <= 0)}
              helperText={expenseForm.amount !== '' && (!Number.isFinite(Number(expenseForm.amount)) || Number(expenseForm.amount) <= 0) ? t('buildingExpenses.amountGreaterThanZero') : ''}
              fullWidth
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>{t('buildingExpenses.category')}</InputLabel>
              <Select
                value={expenseForm.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                {Object.keys(categories.categories || {}).map((category) => (
                  <MenuItem key={category} value={category}>
                    {t(`buildingExpenses.values.${category}`, category.replace('_', ' '))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('buildingExpenses.subcategory')}</InputLabel>
              <Select
                value={expenseForm.subcategory}
                onChange={(e) => handleInputChange('subcategory', e.target.value)}
                disabled={!expenseForm.category}
              >
                {(categories.categories?.[expenseForm.category] || []).map((subcategory) => (
                  <MenuItem key={subcategory} value={subcategory}>
                    {subcategory}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label={t('buildingExpenses.description')}
              value={expenseForm.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('buildingExpenses.vendorName')}
              value={expenseForm.vendorName}
              onChange={(e) => handleInputChange('vendorName', e.target.value)}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('buildingExpenses.invoiceNumber')}
              value={expenseForm.invoiceNumber}
              onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('buildingExpenses.paymentMethod')}</InputLabel>
              <Select
                value={expenseForm.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              >
                {(categories.paymentMethods || []).map((method) => (
                  <MenuItem key={method} value={method}>
                    {method.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('buildingExpenses.urgencyLevel')}</InputLabel>
              <Select
                value={expenseForm.urgencyLevel}
                onChange={(e) => handleInputChange('urgencyLevel', e.target.value)}
              >
                {(categories.urgencyLevels || []).map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('buildingExpenses.allocationType')}</InputLabel>
              <Select
                value={expenseForm.allocationType}
                onChange={(e) => handleInputChange('allocationType', e.target.value)}
              >
                {(categories.allocationTypes || []).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('buildingExpenses.taxAmount')}
              type="number"
              value={expenseForm.taxAmount}
              onChange={(e) => handleInputChange('taxAmount', e.target.value)}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('buildingExpenses.taxRate')}
              type="number"
              value={expenseForm.taxRate}
              onChange={(e) => handleInputChange('taxRate', e.target.value)}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('buildingExpenses.receiptScreenshot')}
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                type="file"
                onChange={(event) => {
                  const file = event.target.files[0];
                  if (file) {
                    // Validate file type
                    if (!file.type.startsWith('image/')) {
                      setError(t('buildingExpenses.invalidImage'));
                      return;
                    }
                    
                    // Validate file size (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                      setError(t('buildingExpenses.fileTooLarge'));
                      return;
                    }

                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setExpenseForm(prev => ({
                        ...prev,
                        photo: e.target.result,
                        photoFileName: file.name
                      }));
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <label htmlFor="photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ReceiptIcon />}
                  fullWidth
                >
                  {expenseForm.photoFileName || t('buildingExpenses.uploadReceipt')}
                </Button>
              </label>
              {expenseForm.photo && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="success.main">
                    ✓ Image uploaded: {expenseForm.photoFileName}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setExpenseForm(prev => ({ ...prev, photo: null, photoFileName: '' }));
                    }}
                    sx={{ ml: 1 }}
                  >
                    {t('common.delete')}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label={t('buildingExpenses.notes')}
              value={expenseForm.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
    </DialogContent>
    <DialogActions sx={{ 
      position: isMobile ? 'sticky' : 'static',
      bottom: 0,
      backgroundColor: 'background.paper',
      borderTop: isMobile ? 1 : 0,
      borderColor: 'divider',
      p: isMobile ? 2 : 1
    }}>
      <Button onClick={onClose} disabled={submitting} sx={{ minHeight: isMobile ? 44 : 'auto' }}>
        {t('common.cancel')}
      </Button>
      <Button 
        onClick={isEditing ? handleUpdateExpense : handleCreateExpense}
        variant="contained"
        disabled={submitting}
        sx={{ minHeight: isMobile ? 44 : 'auto' }}
      >
        {submitting ? <CircularProgress size={20} /> : (isEditing ? t('actions.update') : t('actions.create'))}
      </Button>
    </DialogActions>
  </Dialog>
);

const BuildingExpenses = ({ buildingId: propBuildingId, userRole: propUserRole }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expenses, setExpenses] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // Message states for user feedback
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Building association state
  const [buildingId, setBuildingId] = useState(propBuildingId || null);
  
  // Determine buildingId and userRole from props, user context, or defaults
  
  // Safely get the user role handling both string and array formats
  let userRole = propUserRole;
  if (!userRole) {
    const groups = user?.groups;
    if (groups) {
      if (Array.isArray(groups) && groups.length > 0) {
        const firstGroup = groups[0];
        if (typeof firstGroup === 'string' && firstGroup.trim()) {
          userRole = firstGroup.toUpperCase();
        } else if (firstGroup && typeof firstGroup === 'object' && firstGroup.name) {
          // Handle object format like { name: "building_admin" }
          userRole = firstGroup.name.toUpperCase();
        }
      } else if (typeof groups === 'string' && groups.trim()) {
        const firstGroup = groups.split(',')[0].trim();
        userRole = firstGroup.toUpperCase();
      }
    }
    if (!userRole) {
      userRole = 'BUILDING_ADMIN';
    }
  }
  
  // Dialog states
  const [openExpenseDialog, setOpenExpenseDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [expenseForm, setExpenseForm] = useState({
    expenseDate: new Date(),
    amount: '',
    category: '',
    subcategory: '',
    description: '',
    vendorName: '',
    paymentMethod: 'CASH',
    urgencyLevel: 'MEDIUM',
    invoiceNumber: '',
    notes: '',
    taxAmount: '',
    taxRate: '',
    allocationType: 'EQUAL',
    photo: null,
    photoFileName: ''
  });
  
  // Filters
  const [filters, setFilters] = useState({
    month: format(new Date(), 'yyyy-MM'),
    category: '',
    approvalStatus: '',
    urgencyLevel: ''
  });

  // Report generation states
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1), // First day of current month
    endDate: new Date(), // Today
    category: '',
    approvalStatus: '',
    urgencyLevel: ''
  });

  // Fetch building associations for the user
  useEffect(() => {
    const fetchBuildingAssociations = async () => {
      if (!user) return;
      if (propBuildingId) {
        setBuildingId(propBuildingId);
        return;
      }
      
      const userId = user.userId || user.attributes?.sub || user.username;
      try {
        const res = await usersAPI.getAssociations(userId);
        // Collect buildingIds and building name from association results
        const buildingAssociations = res.data || [];
        
        const ids = buildingAssociations.map(item => item.buildingId);
        if (ids?.length > 0) {
          setBuildingId(ids[0]);
        }
      } catch (err) {
        console.error('Failed to fetch building associations:', err);
      } finally {
        // Continue with default buildingId if no associations found
      }
    };
    
    fetchBuildingAssociations();
  }, [user, propBuildingId]);

  useEffect(() => {
    loadExpenseCategories();
  }, []);

  useEffect(() => {
    if (buildingId) {
      loadExpenses();
      loadMonthlySummary();
    }
  }, [buildingId, filters.month, filters.category, filters.approvalStatus, filters.urgencyLevel]);



  const loadExpenseCategories = async () => {
    try {
      const response = await expensesAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load expense categories:', error);
    }
  };

  const loadExpenses = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await expensesAPI.getAll(buildingId, filters);
      console.log('Load expenses response:', response.data);
      
      const allExpenses = response.data.expenses || response.data || [];
      
      // Log records without expenseId for debugging
      const expensesWithoutId = allExpenses.filter(expense => !expense.expenseId);
      if (expensesWithoutId.length > 0) {
        console.warn(`Found ${expensesWithoutId.length} expense records without expenseId:`, expensesWithoutId);
      }
      
      // Set all expenses (filtering will be done in the render)
      setExpenses(allExpenses);
    } catch (error) {
      console.error('Failed to load expenses:', error);
      setError(t('buildingExpenses.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlySummary = async () => {
    try {
      const response = await expensesAPI.getMonthlySummary(buildingId, filters.month);
      setMonthlySummary(response.data.summary);
    } catch (error) {
      console.error('Failed to load monthly summary:', error);
    }
  };

  const handleCreateExpense = async () => {
    const amount = Number(expenseForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('buildingExpenses.invalidAmount'));
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      
      const expenseData = {
        ...expenseForm,
        expenseDate: format(expenseForm.expenseDate, 'yyyy-MM-dd'),
        amount: parseFloat(expenseForm.amount),
        taxAmount: expenseForm.taxAmount ? parseFloat(expenseForm.taxAmount) : 0,
        taxRate: expenseForm.taxRate ? parseFloat(expenseForm.taxRate) : 0,
        photo: expenseForm.photo,
        photoFileName: expenseForm.photoFileName
      };

      console.log('Creating expense:', expenseData);
      const response = await expensesAPI.create(buildingId, expenseData);
      console.log('Create expense response:', response.data);
      
      if (response.data) {
        setSuccess(t('buildingExpenses.createSuccess'));
        setOpenExpenseDialog(false);
        resetExpenseForm();
        
        // Refresh data
        await loadExpenses();
        await loadMonthlySummary();
      }
    } catch (error) {
      console.error('Failed to create expense:', error);
      setError(error.response?.data?.message || t('buildingExpenses.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateExpense = async () => {
    const amount = Number(expenseForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('buildingExpenses.invalidAmount'));
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      
      const response = await expensesAPI.update(
        selectedExpense.expenseId, 
        buildingId, 
        expenseForm
      );
      
      if (response.data) {
        setSuccess(t('buildingExpenses.updateSuccess'));
        setOpenExpenseDialog(false);
        setIsEditing(false);
        resetExpenseForm();
        loadExpenses();
        loadMonthlySummary();
      }
    } catch (error) {
      console.error('Failed to update expense:', error);
      setError(t('buildingExpenses.updateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveExpense = async (expenseId, status) => {
    try {
      setError('');
      setSuccess('');
      
      const response = await expensesAPI.approve(
        expenseId, 
        buildingId, 
        { status, notes: `${status} by admin` }
      );
      
      if (response.data) {
        setSuccess(t('buildingExpenses.statusSuccess', { status: t(`buildingExpenses.values.${status}`, status) }));
        loadExpenses();
        loadMonthlySummary();
      }
    } catch (error) {
      console.error('Failed to approve expense:', error);
      setError(t('buildingExpenses.statusFailed'));
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        setError('');
        setSuccess('');
        
        const response = await expensesAPI.delete(expenseId, buildingId);
        
        if (response.data) {
          setSuccess(t('buildingExpenses.deleteSuccess'));
          loadExpenses();
          loadMonthlySummary();
        }
      } catch (error) {
        console.error('Failed to delete expense:', error);
        setError(t('buildingExpenses.deleteFailed'));
      }
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      expenseDate: new Date(),
      amount: '',
      category: '',
      subcategory: '',
      description: '',
      vendorName: '',
      paymentMethod: 'CASH',
      urgencyLevel: 'MEDIUM',
      invoiceNumber: '',
      notes: '',
      taxAmount: '',
      taxRate: '',
      allocationType: 'EQUAL',
      photo: null,
      photoFileName: ''
    });
    setSelectedExpense(null);
  };  const handleUploadReceipt = async (expenseId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await expensesAPI.uploadReceipt(buildingId, expenseId, formData);
      
      if (response.data) {
        console.log('Receipt uploaded successfully:', response.data);
        // Refresh the expense to get the updated receipt URL
        loadExpenses();
      }
    } catch (error) {
      console.error('Failed to upload receipt:', error);
    }
  };

  const openEditDialog = (expense) => {
    setSelectedExpense(expense);
    setExpenseForm({
      ...expense,
      expenseDate: new Date(expense.expenseDate)
    });
    setIsEditing(true);
    setOpenExpenseDialog(true);
  };

  const openViewExpenseDialog = (expense) => {
    setSelectedExpense(expense);
    setOpenViewDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING': return 'warning';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'EMERGENCY': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Get filtered expenses for reports
  const getFilteredExpensesForReport = () => {
    return expenses.filter(expense => {
      if (!expense.expenseId) return false;
      
      const expenseDate = new Date(expense.expenseDate);
      const startDate = new Date(reportFilters.startDate);
      const endDate = new Date(reportFilters.endDate);
      
      // Date range filter
      if (expenseDate < startDate || expenseDate > endDate) return false;
      
      // Category filter
      if (reportFilters.category && expense.category !== reportFilters.category) return false;
      
      // Approval status filter
      if (reportFilters.approvalStatus && expense.approvalStatus !== reportFilters.approvalStatus) return false;
      
      // Urgency level filter
      if (reportFilters.urgencyLevel && expense.urgencyLevel !== reportFilters.urgencyLevel) return false;
      
      return true;
    });
  };

  // Export to Excel function
  const exportToExcel = async () => {
    setReportLoading(true);
    try {
      const filteredExpenses = getFilteredExpensesForReport();
      
      if (filteredExpenses.length === 0) {
        setError(t('buildingExpenses.noExpensesForFilters'));
        return;
      }

      // Prepare data for Excel
      const excelData = filteredExpenses.map(expense => ({
        'Expense ID': expense.expenseId,
        'Date': expense.expenseDate,
        'Description': expense.description,
        'Category': expense.category,
        'Subcategory': expense.subcategory || '',
        'Amount (INR)': expense.amount,
        'Vendor': expense.vendorName || '',
        'Payment Method': expense.paymentMethod,
        'Status': expense.approvalStatus,
        'Urgency': expense.urgencyLevel,
        'Invoice Number': expense.invoiceNumber || '',
        'Tax Amount': expense.taxAmount || 0,
        'Notes': expense.notes || '',
        'Created Date': new Date(expense.createdAt).toLocaleDateString(),
        'Logged By': expense.loggedBy
      }));

      // Calculate totals
      const totalAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
      const approvedAmount = filteredExpenses
        .filter(expense => expense.approvalStatus === 'APPROVED')
        .reduce((sum, expense) => sum + expense.amount, 0);
      const pendingAmount = filteredExpenses
        .filter(expense => expense.approvalStatus === 'PENDING')
        .reduce((sum, expense) => sum + expense.amount, 0);

      // Add summary row
      excelData.push({});
      excelData.push({
        'Expense ID': 'SUMMARY',
        'Date': '',
        'Description': 'Total Expenses',
        'Category': '',
        'Subcategory': '',
        'Amount (INR)': totalAmount,
        'Vendor': '',
        'Payment Method': '',
        'Status': '',
        'Urgency': '',
        'Invoice Number': '',
        'Tax Amount': '',
        'Notes': `Total: ${formatCurrency(totalAmount)}, Approved: ${formatCurrency(approvedAmount)}, Pending: ${formatCurrency(pendingAmount)}`,
        'Created Date': '',
        'Logged By': ''
      });

      // Create and download Excel file using a simple method
      const csvContent = convertToCSV(excelData);
      downloadFile(csvContent, `expense_report_${format(new Date(), 'yyyy-MM-dd')}.csv`, 'text/csv');
      
      setSuccess(t('buildingExpenses.excelSuccess'));
    } catch (error) {
      console.error('Excel export error:', error);
      setError(t('buildingExpenses.excelFailed'));
    } finally {
      setReportLoading(false);
    }
  };

  // Convert data to CSV format
  const convertToCSV = (data) => {
    if (!data.length) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [];
    
    // Add headers
    csvRows.push(headers.join(','));
    
    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  };

  // Export to PDF function
  const exportToPDF = async () => {
    setReportLoading(true);
    try {
      const filteredExpenses = getFilteredExpensesForReport();
      
      if (filteredExpenses.length === 0) {
        setError(t('buildingExpenses.noExpensesForFilters'));
        return;
      }

      // Create PDF content
      const pdfContent = generatePDFContent(filteredExpenses);
      
      // For now, create a simple HTML version that can be printed as PDF
      const printWindow = window.open('', '_blank');
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      printWindow.print();
      
      setSuccess(t('buildingExpenses.pdfSuccess'));
    } catch (error) {
      console.error('PDF export error:', error);
      setError(t('buildingExpenses.pdfFailed'));
    } finally {
      setReportLoading(false);
    }
  };

  // Generate PDF HTML content
  const generatePDFContent = (filteredExpenses) => {
    const totalAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const approvedAmount = filteredExpenses
      .filter(expense => expense.approvalStatus === 'APPROVED')
      .reduce((sum, expense) => sum + expense.amount, 0);
    const pendingAmount = filteredExpenses
      .filter(expense => expense.approvalStatus === 'PENDING')
      .reduce((sum, expense) => sum + expense.amount, 0);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Expense Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #1976d2; text-align: center; }
          .summary { background: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #1976d2; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .status-approved { color: green; font-weight: bold; }
          .status-pending { color: orange; font-weight: bold; }
          .status-rejected { color: red; font-weight: bold; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>Building Expense Report</h1>
        <div class="summary">
          <h3>Summary</h3>
          <p><strong>Report Period:</strong> ${format(reportFilters.startDate, 'dd/MM/yyyy')} to ${format(reportFilters.endDate, 'dd/MM/yyyy')}</p>
          <p><strong>Total Expenses:</strong> ${formatCurrency(totalAmount)}</p>
          <p><strong>Approved Amount:</strong> ${formatCurrency(approvedAmount)}</p>
          <p><strong>Pending Amount:</strong> ${formatCurrency(pendingAmount)}</p>
          <p><strong>Total Records:</strong> ${filteredExpenses.length}</p>
          <p><strong>Generated On:</strong> ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Expense ID</th>
              <th>Date</th>
              <th>Description</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Vendor</th>
              <th>Payment Method</th>
            </tr>
          </thead>
          <tbody>
            ${filteredExpenses.map(expense => `
              <tr>
                <td>${expense.expenseId}</td>
                <td>${expense.expenseDate}</td>
                <td>${expense.description}</td>
                <td>${expense.category}</td>
                <td>${formatCurrency(expense.amount)}</td>
                <td class="status-${expense.approvalStatus.toLowerCase()}">${expense.approvalStatus}</td>
                <td>${expense.vendorName || '-'}</td>
                <td>${expense.paymentMethod}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
  };

  // Download file helper
  const downloadFile = (content, fileName, contentType) => {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleInputChange = (field, value) => {
    setExpenseForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryChange = (value) => {
    setExpenseForm(prev => ({
      ...prev,
      category: value,
      subcategory: '' // Reset subcategory when category changes
    }));
  };

  const ViewExpenseDialog = () => (
    <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ 
        position: isMobile ? 'sticky' : 'static',
        top: 0,
        zIndex: 1,
        backgroundColor: 'background.paper',
        borderBottom: isMobile ? 1 : 0,
        borderColor: 'divider'
      }}>
        {t('buildingExpenses.expenseDetails')}
      </DialogTitle>
      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        {selectedExpense && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">{t('buildingExpenses.expenseId')}</Typography>
              <Typography variant="body1">{selectedExpense.expenseId}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">{t('buildingExpenses.date')}</Typography>
              <Typography variant="body1">{selectedExpense.expenseDate}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">{t('buildingExpenses.amount')}</Typography>
              <Typography variant="body1">{formatCurrency(selectedExpense.amount)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">{t('buildingExpenses.status')}</Typography>
              <Chip 
                label={t(`buildingExpenses.${selectedExpense.approvalStatus?.toLowerCase()}`, selectedExpense.approvalStatus)}
                color={getStatusColor(selectedExpense.approvalStatus)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">{t('buildingExpenses.category')}</Typography>
              <Typography variant="body1">{selectedExpense.category}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">{t('buildingExpenses.vendor')}</Typography>
              <Typography variant="body1">{selectedExpense.vendorName || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('buildingExpenses.description')}</Typography>
              <Typography variant="body1">{selectedExpense.description}</Typography>
            </Grid>
            {selectedExpense.receiptUrl && (
              <Grid item xs={12}>
                <Button
                  startIcon={<ReceiptIcon />}
                  onClick={() => window.open(selectedExpense.receiptUrl, '_blank')}
                >
                  {t('buildingExpenses.viewReceipt')}
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        position: isMobile ? 'sticky' : 'static',
        bottom: 0,
        backgroundColor: 'background.paper',
        borderTop: isMobile ? 1 : 0,
        borderColor: 'divider',
        p: isMobile ? 2 : 1
      }}>
        <Button onClick={() => setOpenViewDialog(false)} sx={{ minHeight: isMobile ? 44 : 'auto' }}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const MonthlySummaryCard = () => (
    <Card variant="outlined" sx={{ mb: 2.5, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: '#f7f9fc', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('buildingExpenses.monthlySummary')} - {filters.month}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('buildingExpenses.monthlySummarySubtitle')}
        </Typography>
      </Box>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {monthlySummary ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary">{t('buildingExpenses.totalExpenses')}</Typography>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                  {formatCurrency(monthlySummary.totalAmount || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary">{t('buildingExpenses.approved')}</Typography>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                  {formatCurrency(monthlySummary.approvedAmount || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary">{t('buildingExpenses.pending')}</Typography>
                <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                  {formatCurrency(monthlySummary.pendingAmount || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary">{t('buildingExpenses.totalEntries')}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {monthlySummary.expenseCount || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Typography>{t('buildingExpenses.loadingSummary')}</Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1280, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
            <ReceiptIcon color="primary" sx={{ fontSize: { xs: 30, sm: 36 } }} />
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, letterSpacing: 0 }}>
              {t('buildingExpenses.title')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('buildingExpenses.subtitle')}
          </Typography>
        </Box>
        {(userRole === 'ADMIN' || userRole === 'BUILDING_ADMIN') && (
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetExpenseForm();
              setIsEditing(false);
              setOpenExpenseDialog(true);
            }}
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, minWidth: { sm: 150 }, fontWeight: 700 }}
          >
            {t('buildingExpenses.addExpense')}
          </Button>
        )}
      </Box>
      </Paper>

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs 
        value={tabValue} 
        onChange={(e, newValue) => setTabValue(newValue)} 
        sx={{ 
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTabs-scrollButtons': {
            '&.Mui-disabled': {
              opacity: 0.3
            }
          },
          '& .MuiTab-root': {
            fontWeight: 700,
            textTransform: 'none',
          }
        }}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile ? "auto" : false}
        allowScrollButtonsMobile={isMobile}
      >
        <Tab label={t('buildingExpenses.expensesTab')} sx={{ fontSize: isMobile ? '14px' : '16px', minHeight: isMobile ? 48 : 'auto' }} />
        <Tab label={t('buildingExpenses.summaryTab')} sx={{ fontSize: isMobile ? '14px' : '16px', minHeight: isMobile ? 48 : 'auto' }} />
        <Tab label={t('buildingExpenses.reportsTab')} sx={{ fontSize: isMobile ? '14px' : '16px', minHeight: isMobile ? 48 : 'auto' }} />
      </Tabs>

      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Card variant="outlined" sx={{ mb: 2.5, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: '#f7f9fc', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('buildingExpenses.filters')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('buildingExpenses.filtersSubtitle')}
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t('buildingExpenses.month')}
                    type="month"
                    value={filters.month}
                    onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel shrink>{t('buildingExpenses.category')}</InputLabel>
                    <Select
                      displayEmpty
                      label={t('buildingExpenses.category')}
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                      renderValue={(value) => value
                        ? t(`buildingExpenses.values.${value}`, value.replace('_', ' '))
                        : t('buildingExpenses.allCategories')}
                    >
                      <MenuItem value="">{t('buildingExpenses.allCategories')}</MenuItem>
                      {Object.keys(categories.categories || {}).map((category) => (
                        <MenuItem key={category} value={category}>
                          {category.replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel shrink>{t('buildingExpenses.status')}</InputLabel>
                    <Select
                      displayEmpty
                      label={t('buildingExpenses.status')}
                      value={filters.approvalStatus}
                      onChange={(e) => setFilters({ ...filters, approvalStatus: e.target.value })}
                      renderValue={(value) => value
                        ? t(`buildingExpenses.${value.toLowerCase()}`, value)
                        : t('buildingExpenses.allStatuses')}
                    >
                      <MenuItem value="">{t('buildingExpenses.allStatuses')}</MenuItem>
                      <MenuItem value="PENDING">{t('buildingExpenses.pending')}</MenuItem>
                      <MenuItem value="APPROVED">{t('buildingExpenses.approved')}</MenuItem>
                      <MenuItem value="REJECTED">{t('buildingExpenses.rejected')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button 
                    variant="contained" 
                    onClick={() => {
                      loadExpenses();
                      loadMonthlySummary();
                    }}
                    fullWidth
                    disabled={loading}
                    sx={{ minHeight: 56, fontSize: isMobile ? '14px' : '16px', fontWeight: 700 }}
                  >
                    {loading ? t('common.loading') : t('buildingExpenses.applyFilters')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <MonthlySummaryCard />

          {/* Expenses Table */}
          {isMobile ? (
            // Mobile card layout
            <Box>
              {loading ? (
                <Card sx={{ mb: 2, p: 2, textAlign: 'center' }}>
                  <CircularProgress />
                </Card>
              ) : expenses.filter(expense => expense.expenseId).length === 0 ? (
                <Card sx={{ mb: 2, p: 2, textAlign: 'center' }}>
                  <Typography variant="body2">{t('buildingExpenses.noExpenses')}</Typography>
                </Card>
              ) : (
                expenses
                  .filter(expense => expense.expenseId)
                  .map((expense) => (
                    <Card key={expense.expenseId} sx={{ mb: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '11px', color: 'text.secondary' }}>
                            {expense.expenseId || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            {expense.expenseDate}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1, fontSize: '14px' }}>
                          {expense.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Chip label={expense.category} size="small" variant="outlined" sx={{ fontSize: '11px' }} />
                          <Chip 
                            label={t(`buildingExpenses.${expense.approvalStatus?.toLowerCase()}`, expense.approvalStatus)}
                            color={getStatusColor(expense.approvalStatus)}
                            size="small"
                            sx={{ fontSize: '11px' }}
                          />
                          <Chip 
                            label={t(`buildingExpenses.${expense.urgencyLevel?.toLowerCase()}`, expense.urgencyLevel)}
                            color={getUrgencyColor(expense.urgencyLevel)}
                            size="small"
                            sx={{ fontSize: '11px' }}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: 'primary.main' }}>
                            {formatCurrency(expense.amount)}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton onClick={() => openViewExpenseDialog(expense)} size="small" sx={{ minHeight: 44 }}>
                              <ViewIcon />
                            </IconButton>
                            {(userRole === 'ADMIN' || userRole === 'BUILDING_ADMIN') && (
                              <>
                                <IconButton onClick={() => openEditDialog(expense)} size="small" sx={{ minHeight: 44 }}>
                                  <EditIcon />
                                </IconButton>
                                {expense.approvalStatus === 'PENDING' && (
                                  <>
                                    <IconButton 
                                      onClick={() => handleApproveExpense(expense.expenseId, 'APPROVED')}
                                      size="small"
                                      color="success"
                                      sx={{ minHeight: 44 }}
                                    >
                                      <ApproveIcon />
                                    </IconButton>
                                    <IconButton 
                                      onClick={() => handleApproveExpense(expense.expenseId, 'REJECTED')}
                                      size="small"
                                      color="error"
                                      sx={{ minHeight: 44 }}
                                    >
                                      <RejectIcon />
                                    </IconButton>
                                  </>
                                )}
                                <IconButton 
                                  onClick={() => handleDeleteExpense(expense.expenseId)} 
                                  size="small"
                                  color="error"
                                  sx={{ minHeight: 44 }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
              )}
            </Box>
          ) : (
            // Desktop table layout
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                '& .MuiTableCell-head': {
                  bgcolor: '#f7f9fc',
                  fontWeight: 700,
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('expenses.expenseId')}</TableCell>
                    <TableCell>{t('expenses.date')}</TableCell>
                    <TableCell>{t('expenses.description')}</TableCell>
                    <TableCell>{t('expenses.category')}</TableCell>
                    <TableCell>{t('expenses.amount')}</TableCell>
                    <TableCell>{t('expenses.status')}</TableCell>
                    <TableCell>{t('buildingExpenses.urgency')}</TableCell>
                    <TableCell>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : expenses.filter(expense => expense.expenseId).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <ReceiptIcon color="disabled" sx={{ fontSize: 42, mb: 1 }} />
                        <Typography color="text.secondary">{t('buildingExpenses.noExpenses')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses
                      .filter(expense => expense.expenseId) // Only show records with expenseId
                      .map((expense) => (
                      <TableRow key={expense.expenseId} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {expense.expenseId || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{expense.expenseDate}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {expense.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={expense.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>
                            {formatCurrency(expense.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={t(`buildingExpenses.${expense.approvalStatus?.toLowerCase()}`, expense.approvalStatus)}
                            color={getStatusColor(expense.approvalStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={t(`buildingExpenses.${expense.urgencyLevel?.toLowerCase()}`, expense.urgencyLevel)}
                            color={getUrgencyColor(expense.urgencyLevel)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => openViewExpenseDialog(expense)} size="small">
                            <ViewIcon />
                          </IconButton>
                          {(userRole === 'ADMIN' || userRole === 'BUILDING_ADMIN') && (
                            <>
                              <IconButton onClick={() => openEditDialog(expense)} size="small">
                                <EditIcon />
                              </IconButton>
                              {expense.approvalStatus === 'PENDING' && (
                                <>
                                  <IconButton 
                                    onClick={() => handleApproveExpense(expense.expenseId, 'APPROVED')}
                                    size="small"
                                    color="success"
                                  >
                                    <ApproveIcon />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => handleApproveExpense(expense.expenseId, 'REJECTED')}
                                    size="small"
                                    color="error"
                                  >
                                    <RejectIcon />
                                  </IconButton>
                                </>
                              )}
                              <IconButton 
                                onClick={() => handleDeleteExpense(expense.expenseId)} 
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tabValue === 1 && (
        <Box>
          <MonthlySummaryCard />
          {/* Add detailed charts and breakdowns here */}
        </Box>
      )}

      {tabValue === 2 && (
        <Box>
          <Typography variant="h4" gutterBottom>
            <ReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('buildingExpenses.expenseReports')}
          </Typography>
          
          {/* Report Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <DateRangeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('buildingExpenses.reportFilters')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <DatePicker
                      label={t('common.startDate')}
                      value={reportFilters.startDate}
                      onChange={(date) => setReportFilters(prev => ({ ...prev, startDate: date }))}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <DatePicker
                      label={t('common.endDate')}
                      value={reportFilters.endDate}
                      onChange={(date) => setReportFilters(prev => ({ ...prev, endDate: date }))}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel shrink>{t('buildingExpenses.category')}</InputLabel>
                      <Select
                        displayEmpty
                        label={t('buildingExpenses.category')}
                        value={reportFilters.category}
                        onChange={(e) => setReportFilters(prev => ({ ...prev, category: e.target.value }))}
                        renderValue={(value) => value
                          ? t(`buildingExpenses.values.${value}`, value.replace('_', ' '))
                          : t('buildingExpenses.allCategories')}
                      >
                        <MenuItem value="">{t('buildingExpenses.allCategories')}</MenuItem>
                        {Object.keys(categories.categories || {}).map((category) => (
                          <MenuItem key={category} value={category}>
                            {category.replace('_', ' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel shrink>{t('buildingExpenses.status')}</InputLabel>
                      <Select
                        displayEmpty
                        label={t('buildingExpenses.status')}
                        value={reportFilters.approvalStatus}
                        onChange={(e) => setReportFilters(prev => ({ ...prev, approvalStatus: e.target.value }))}
                        renderValue={(value) => value
                          ? t(`buildingExpenses.${value.toLowerCase()}`, value)
                          : t('buildingExpenses.allStatuses')}
                      >
                        <MenuItem value="">{t('buildingExpenses.allStatuses')}</MenuItem>
                        <MenuItem value="PENDING">{t('buildingExpenses.pending')}</MenuItem>
                        <MenuItem value="APPROVED">{t('buildingExpenses.approved')}</MenuItem>
                        <MenuItem value="REJECTED">{t('buildingExpenses.rejected')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel shrink>{t('buildingExpenses.urgency')}</InputLabel>
                      <Select
                        displayEmpty
                        label={t('buildingExpenses.urgency')}
                        value={reportFilters.urgencyLevel}
                        onChange={(e) => setReportFilters(prev => ({ ...prev, urgencyLevel: e.target.value }))}
                        renderValue={(value) => value
                          ? t(`buildingExpenses.${value.toLowerCase()}`, value)
                          : t('buildingExpenses.all')}
                      >
                        <MenuItem value="">{t('buildingExpenses.all')}</MenuItem>
                        <MenuItem value="LOW">{t('buildingExpenses.low')}</MenuItem>
                        <MenuItem value="MEDIUM">{t('buildingExpenses.medium')}</MenuItem>
                        <MenuItem value="HIGH">{t('buildingExpenses.high')}</MenuItem>
                        <MenuItem value="EMERGENCY">{t('buildingExpenses.emergency')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </CardContent>
          </Card>

          {/* Report Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('buildingExpenses.reportSummary')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">{t('buildingExpenses.totalRecords')}</Typography>
                  <Typography variant="h5" color="primary">
                    {getFilteredExpensesForReport().length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">{t('buildingExpenses.totalAmount')}</Typography>
                  <Typography variant="h5" color="primary">
                    {formatCurrency(getFilteredExpensesForReport().reduce((sum, expense) => sum + expense.amount, 0))}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">{t('buildingExpenses.approvedAmount')}</Typography>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(getFilteredExpensesForReport()
                      .filter(expense => expense.approvalStatus === 'APPROVED')
                      .reduce((sum, expense) => sum + expense.amount, 0))}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">{t('buildingExpenses.pendingAmount')}</Typography>
                  <Typography variant="h6" color="warning.main">
                    {formatCurrency(getFilteredExpensesForReport()
                      .filter(expense => expense.approvalStatus === 'PENDING')
                      .reduce((sum, expense) => sum + expense.amount, 0))}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Export Options */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('buildingExpenses.exportOptions')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    startIcon={reportLoading ? <CircularProgress size={20} /> : <ExcelIcon />}
                    onClick={exportToExcel}
                    disabled={reportLoading || getFilteredExpensesForReport().length === 0}
                    fullWidth
                    size="large"
                    sx={{ py: 2 }}
                  >
                    {reportLoading ? t('buildingExpenses.generating') : t('buildingExpenses.exportExcel')}
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                    {t('buildingExpenses.excelHelp')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={reportLoading ? <CircularProgress size={20} /> : <PdfIcon />}
                    onClick={exportToPDF}
                    disabled={reportLoading || getFilteredExpensesForReport().length === 0}
                    fullWidth
                    size="large"
                    sx={{ py: 2 }}
                  >
                    {reportLoading ? t('buildingExpenses.generating') : t('buildingExpenses.exportPdf')}
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                    {t('buildingExpenses.pdfHelp')}
                  </Typography>
                </Grid>
              </Grid>
              
              {getFilteredExpensesForReport().length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('buildingExpenses.noExpensesAdjustFilters')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      <ExpenseDialog
        open={openExpenseDialog}
        onClose={() => setOpenExpenseDialog(false)}
        isEditing={isEditing}
        submitting={submitting}
        expenseForm={expenseForm}
        categories={categories}
        handleInputChange={handleInputChange}
        handleCategoryChange={handleCategoryChange}
        setError={setError}
        setExpenseForm={setExpenseForm}
        handleCreateExpense={handleCreateExpense}
        handleUpdateExpense={handleUpdateExpense}
        isMobile={isMobile}
        error={error}
        t={t}
      />
      <ViewExpenseDialog />
    </Box>
  );
};

export default BuildingExpenses;
